/**
 * useLiveRefresh — app-wide real-time data synchronization (Jun 2026).
 *
 * Subscribes to server `data_event` broadcasts (fired after every successful
 * mutating API call by ANY user) and re-runs the component's fetch function
 * when a relevant topic changes. Debounced so bursts coalesce into one
 * refetch. Falls back to interval polling while the WebSocket is down.
 *
 * Usage:
 *   useLiveRefresh(['jobs', 'applications'], loadJobs);
 *   useLiveRefresh('*', loadEverything, { debounceMs: 1200 });
 */
import { useEffect, useRef } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';

export function useLiveRefresh(topics, refetch, { debounceMs = 900, pollMs = 45000, enabled = true } = {}) {
  const { subscribeData, isConnected } = useWebSocket();
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const topicsRef = useRef(topics);
  topicsRef.current = topics;
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const unsubscribe = subscribeData((evt) => {
      const t = topicsRef.current;
      const relevant = t === '*' || (Array.isArray(evt.topics) && evt.topics.some((x) => t.includes(x)));
      if (!relevant) return;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (document.visibilityState !== 'hidden') refetchRef.current?.(evt);
      }, debounceMs);
    });
    return () => {
      unsubscribe();
      clearTimeout(timerRef.current);
    };
  }, [subscribeData, debounceMs, enabled]);

  // Polling fallback: only while the realtime connection is unavailable.
  useEffect(() => {
    if (!enabled || isConnected || !pollMs) return undefined;
    const iv = setInterval(() => {
      if (document.visibilityState !== 'hidden') refetchRef.current?.({ topics: ['poll'] });
    }, pollMs);
    return () => clearInterval(iv);
  }, [isConnected, pollMs, enabled]);
}

export default useLiveRefresh;
